/**
 * Client-side tile registry implementing the H2 TileRegistry interface.
 *
 * platform-dev owns this registry. Adapters are registered by type — the
 * renderer looks up by tile type, never imports a concrete adapter directly
 * (ADR-001 H2 decoupling). This keeps the layout engine independent of the
 * tile library.
 *
 * Immutability: the registry is built once at startup; the returned Map is
 * frozen. Adding adapters at runtime is not supported (and not needed — the
 * tile set is closed for Phase 1).
 */
import type { TileType } from 'briefpane/schema';
import type { ClientAdapter, TileRegistry } from 'briefpane/contracts';

/**
 * Build an immutable TileRegistry from an array of ClientAdapters.
 * Pure — returns a fresh frozen registry, never mutates input.
 */
export function buildRegistry(adapters: readonly ClientAdapter[]): TileRegistry {
  const map = new Map<TileType, ClientAdapter>(
    adapters.map((a) => [a.type, a]),
  );
  Object.freeze(map);

  return Object.freeze({
    get(type: TileType): ClientAdapter | undefined {
      return map.get(type);
    },
    has(type: TileType): boolean {
      return map.has(type);
    },
    types(): readonly TileType[] {
      return Object.freeze([...map.keys()]);
    },
  });
}
